import { createColumnHelper } from "@tanstack/react-table";
import _, { groupBy } from "lodash";
import { useEffect, useMemo, useState } from "react";
import { Alert, Button } from "react-bootstrap";
import type {
  Account,
  AccountHoldingResponse,
} from "../../../api/src/accounts/accounts.service";
import type { Transaction } from "../../../api/src/transactions/lib";
import Loading from "../Loading";
import { capitalize, displayAccountName } from "../lib/display";
import { formatCurrencyComponent, formatPercent } from "../lib/numbers";
import { CASH_SYMBOLS } from "../lib/security";
import SecurityLink from "./SecurityLink";
import { Table, useTable } from "./Table/Table";

type AccountHoldingTotal = {
  value: number;
  realizedGainLoss: number;
  unrealizedGainLoss: number;
  weight: number;
  modelWeight: number;
  modelWeightDifference: number;
  unrealizedGainLossPercent: number;
  income: number;
};

type AccountHoldingRow = {
  securityId: number;
  riskAllocation: string;
  categoryOrder: number;
  security: AccountHoldingResponse["security"];
  totals: AccountHoldingTotal;
  accounts: Record<number, AccountHoldingTotal>;
};

export type HoldingsFieldMode =
  | "value"
  | "weight"
  | "modelWeight"
  | "modelWeightDifference"
  | "realizedGainLoss"
  | "unrealizedGainLoss"
  | "unrealizedGainLossPercent"
  | "income";

function getTableValue(fieldMode: HoldingsFieldMode, value: number) {
  return value === 0
    ? "–"
    : fieldMode === "weight" ||
        fieldMode === "modelWeight" ||
        fieldMode === "modelWeightDifference" ||
        fieldMode === "unrealizedGainLossPercent"
      ? formatPercent(value, 2)
      : formatCurrencyComponent(value, 2);
}

const emptyTotal: AccountHoldingTotal = {
  value: 0,
  unrealizedGainLoss: 0,
  realizedGainLoss: 0,
  weight: 0,
  modelWeight: 0,
  modelWeightDifference: 0,
  unrealizedGainLossPercent: 0,
  income: 0,
};

/**
 * The key of a holding's row: its symbol when the table shows one row for
 * each symbol (APP-2211) and the symbol is not empty, else its security id.
 */
function getRowKey(
  holding: Pick<AccountHoldingResponse, "securityId" | "security">,
  oneRowPerSymbol: boolean,
) {
  const symbol = holding.security?.symbol;
  return oneRowPerSymbol && symbol
    ? `symbol-${symbol}`
    : `${holding.securityId}`;
}

export function createRowsFromHoldings(
  holdings: AccountHoldingResponse[],
  transactions: Transaction[],
  modelSummaries: {
    accountId: number;
    model: Record<string, number>;
  }[],
  riskCategories: string[],
  { oneRowPerSymbol = false }: { oneRowPerSymbol?: boolean } = {},
) {
  const cashHoldings = holdings.filter((holding) =>
    CASH_SYMBOLS.includes(holding.security?.symbol ?? ""),
  );
  const nonCashHoldings = holdings.filter(
    (holding) => !CASH_SYMBOLS.includes(holding.security?.symbol ?? ""),
  );

  // Build dynamic category map from firm risk categories
  const allHoldings: Record<string, AccountHoldingResponse[]> = {};
  for (const category of riskCategories) {
    allHoldings[category] = nonCashHoldings.filter(
      (h) => (h.security?.riskAllocation?.[category] ?? 0) > 0,
    );
  }
  // Always add cash at end
  allHoldings["Cash"] = cashHoldings;

  const transactionsBySecurity = groupBy(transactions, "securityId");
  const modelSummariesMap = _.keyBy(modelSummaries, "accountId");

  const totalValue = holdings.reduce((sum, holding) => sum + holding.value, 0);

  // Use explicit category order: firm risk categories followed by Cash
  const orderedCategories = [...riskCategories, "Cash"];

  return orderedCategories.flatMap((riskAllocationCategory, categoryIndex) => {
    const riskAllocationHoldings = allHoldings[riskAllocationCategory] ?? [];
    const holdingsByRow = groupBy(riskAllocationHoldings, (holding) =>
      getRowKey(holding, oneRowPerSymbol),
    );
    const getRiskAllocationRatio = (
      security: AccountHoldingResponse["security"],
    ) =>
      riskAllocationCategory === "Cash"
        ? 1
        : (security?.riskAllocation?.[riskAllocationCategory] ?? 0);

    // One row for each security id (the App trade proposal Overview): the
    // share and the income of the row's security apply to each holding.
    const createSecurityAccountTotal = (
      accountId: string,
      holdings: AccountHoldingResponse[],
    ): AccountHoldingTotal => {
      const security = holdings[0].security;
      const riskAllocationRatio = getRiskAllocationRatio(security);
      const transactionsByAccount = groupBy(
        transactionsBySecurity[holdings[0].securityId] ?? [],
        "accountId",
      );

      return holdings.reduce((sum, holding): AccountHoldingTotal => {
        const weight =
          sum.weight +
          (totalValue === 0 ? 0 : holding.value / totalValue) *
            riskAllocationRatio;
        const modelWeight =
          sum.modelWeight +
          (typeof security?.symbol === "undefined"
            ? 0
            : (modelSummariesMap[accountId]?.model[security.symbol] ?? 0)) *
            riskAllocationRatio;

        const value = sum.value + (holding.value ?? 0) * riskAllocationRatio;
        const unrealizedGainLoss =
          sum.unrealizedGainLoss +
          // `null` for a security with realized gains only
          // (APP-1653): no position, so nothing to add, as when the
          // API sent `0`.
          (holding.unrealizedGainLoss ?? 0) * riskAllocationRatio;
        const costBasis = value - unrealizedGainLoss;
        const unrealizedGainLossPercent =
          costBasis > 0 ? unrealizedGainLoss / costBasis : 0;

        return {
          value,
          unrealizedGainLoss,
          realizedGainLoss:
            sum.realizedGainLoss +
            (holding.realizedGainLoss ?? 0) * riskAllocationRatio,
          weight,
          modelWeight,
          modelWeightDifference: weight - modelWeight,
          unrealizedGainLossPercent:
            sum.unrealizedGainLossPercent + unrealizedGainLossPercent,
          income:
            sum.income +
            (transactionsByAccount[accountId] ?? []).reduce(
              (sum, transaction) => sum + (transaction.amount ?? 0),
              0,
            ) *
              riskAllocationRatio,
        };
      }, emptyTotal);
    };

    // One row for each symbol (the App household Portfolio tab, APP-2211):
    // each holding counts with the share of its own security, the income of
    // each security id of the row counts once, and the model weight of the
    // symbol counts once for the account.
    const createSymbolAccountTotal = (
      accountId: string,
      holdings: AccountHoldingResponse[],
    ): AccountHoldingTotal => {
      const total = holdings.reduce(
        (sum, holding) => {
          const ratio = getRiskAllocationRatio(holding.security);
          return {
            value: sum.value + (holding.value ?? 0) * ratio,
            // `null` for a security with realized gains only (APP-1653).
            unrealizedGainLoss:
              sum.unrealizedGainLoss +
              (holding.unrealizedGainLoss ?? 0) * ratio,
            realizedGainLoss:
              sum.realizedGainLoss + (holding.realizedGainLoss ?? 0) * ratio,
            weight:
              sum.weight +
              (totalValue === 0 ? 0 : holding.value / totalValue) * ratio,
            // The share of the model weight is the shares of the holdings
            // weighted by value (by count when the holdings have no value).
            ratioByValue: sum.ratioByValue + (holding.value ?? 0) * ratio,
            ratioByCount: sum.ratioByCount + ratio / holdings.length,
            accountValue: sum.accountValue + (holding.value ?? 0),
          };
        },
        {
          value: 0,
          unrealizedGainLoss: 0,
          realizedGainLoss: 0,
          weight: 0,
          ratioByValue: 0,
          ratioByCount: 0,
          accountValue: 0,
        },
      );
      const modelWeightRatio =
        total.accountValue === 0
          ? total.ratioByCount
          : total.ratioByValue / total.accountValue;
      const symbol = holdings[0].security?.symbol;
      const modelWeight =
        (symbol ? (modelSummariesMap[accountId]?.model[symbol] ?? 0) : 0) *
        modelWeightRatio;

      const income = _.uniqBy(holdings, "securityId").reduce(
        (sum, holding) =>
          sum +
          (transactionsBySecurity[holding.securityId] ?? [])
            .filter((transaction) => `${transaction.accountId}` === accountId)
            .reduce((sum, transaction) => sum + (transaction.amount ?? 0), 0) *
            getRiskAllocationRatio(holding.security),
        0,
      );

      const costBasis = total.value - total.unrealizedGainLoss;

      return {
        value: total.value,
        unrealizedGainLoss: total.unrealizedGainLoss,
        realizedGainLoss: total.realizedGainLoss,
        weight: total.weight,
        modelWeight,
        modelWeightDifference: total.weight - modelWeight,
        unrealizedGainLossPercent:
          costBasis > 0 ? total.unrealizedGainLoss / costBasis : 0,
        income,
      };
    };

    return Object.values(holdingsByRow).map(
      (securityHoldings): AccountHoldingRow => {
        const security = securityHoldings[0].security;
        const holdingsByAccount = groupBy(securityHoldings, "accountId");

        const holdingsByAccountTotals = Object.entries(
          holdingsByAccount,
        ).reduce<Record<number, AccountHoldingTotal>>(
          (result, [accountId, holdings]) => ({
            ...result,
            [parseInt(accountId)]: oneRowPerSymbol
              ? createSymbolAccountTotal(accountId, holdings)
              : createSecurityAccountTotal(accountId, holdings),
          }),
          {},
        );

        return {
          securityId: securityHoldings[0].securityId,
          riskAllocation: riskAllocationCategory,
          categoryOrder: categoryIndex,
          security,
          totals: Object.values(
            holdingsByAccountTotals,
          ).reduce<AccountHoldingTotal>((sum, accountTotals) => {
            const newTotalValue = sum.value + accountTotals.value;
            const weight =
              sum.weight +
              (totalValue === 0 ? 0 : accountTotals.value / totalValue);
            const modelWeight = sum.modelWeight + accountTotals.modelWeight;

            const totalUnrealizedGainLoss =
              sum.unrealizedGainLoss + accountTotals.unrealizedGainLoss;
            const totalCostBasis = newTotalValue - totalUnrealizedGainLoss;
            const totalUnrealizedGainLossPercent =
              totalCostBasis > 0 ? totalUnrealizedGainLoss / totalCostBasis : 0;

            return {
              value: newTotalValue,
              unrealizedGainLoss: totalUnrealizedGainLoss,
              realizedGainLoss:
                sum.realizedGainLoss + accountTotals.realizedGainLoss,
              weight,
              modelWeight,
              modelWeightDifference: weight - modelWeight,
              unrealizedGainLossPercent: totalUnrealizedGainLossPercent,
              income: sum.income + accountTotals.income,
            };
          }, emptyTotal),
          accounts: holdingsByAccountTotals,
        };
      },
    );
  });
}

const HoldingsByAccountTable = ({
  holdings,
  grouped = "riskAllocation",
  accounts,
  modelSummaries,
  transactions,
  fieldMode = "value",
  isLoading = false,
  riskCategories,
  keepZeroValueInModelWeight = false,
  oneRowPerSymbol = false,
}: {
  holdings: AccountHoldingResponse[];
  grouped?: "riskAllocation";
  accounts?: Account[];
  modelSummaries: {
    accountId: number;
    model: Record<string, number>;
  }[];
  transactions: Transaction[];
  fieldMode?: HoldingsFieldMode;
  isLoading?: boolean;
  riskCategories: string[];
  /** Keep a row with value 0 in the model weight modes. */
  keepZeroValueInModelWeight?: boolean;
  /** Show one row for each symbol in each group, not each security id. */
  oneRowPerSymbol?: boolean;
}) => {
  const columnHelper = useMemo(
    () => createColumnHelper<AccountHoldingRow>(),
    [],
  );

  const [isExpanded, setIsExpanded] = useState(false);

  const flatColumns = useMemo(
    () => [
      // Hidden column for sorting by category order
      columnHelper.accessor((row) => row.categoryOrder, {
        id: "categoryOrder",
        header: () => null,
        cell: () => null,
        enableColumnFilter: false,
        meta: {
          className: "d-none",
          headerClassName: "d-none",
        },
      }),
      columnHelper.accessor((row) => row.security?.identifier ?? "", {
        id: "symbol",
        header: () => "Symbol",
        cell: (info) => (
          <SecurityLink
            symbol={info.getValue()}
            description={info.row.original.security?.description}
          />
        ),
        minSize: 200,
        size: 200,
        enableColumnFilter: false,
        meta: {
          className: "position-sticky",
          headerClassName: "position-sticky z-1",
          headerStyle: { left: 180 },
          style: { left: 180 },
        },
      }),
      columnHelper.accessor((row) => row.totals?.[fieldMode] ?? 0, {
        id: "totalValue",
        cell: (info) => getTableValue(fieldMode, info.getValue()),
        header: () => "All Selected Accounts",
        aggregatedCell: (info) => (
          <span className="fw-bold">
            {getTableValue(fieldMode, info.getValue())}
          </span>
        ),
        minSize: 180,
        size: 180,
        enableColumnFilter: false,
        meta: {
          className: "text-end position-sticky",
          headerClassName: "text-end position-sticky z-1",
          headerStyle: { left: 380 },
          style: { left: 380 },
        },
      }),
      columnHelper.display({
        id: "expander",
        header: () => (
          <Button onClick={() => setIsExpanded(!isExpanded)}>
            {isExpanded ? "<" : ">"}
          </Button>
        ),
        meta: {
          className: `position-sticky ${!isExpanded ? "" : "border-end"}`,
          headerClassName: `position-sticky z-1 ${
            !isExpanded ? "" : "border-end"
          }`,
          headerStyle: { left: 560 },
          style: { left: 560 },
        },
      }),
      ...(!isExpanded
        ? []
        : (accounts ?? []).map((account) =>
            columnHelper.accessor(
              (row) => row.accounts[account.id]?.[fieldMode] ?? 0,
              {
                id: `account-${account.id}-${fieldMode}`,
                cell: (info) => getTableValue(fieldMode, info.getValue()),
                header: () =>
                  displayAccountName(
                    account.displayName,
                    account.displayNumber,
                  ),
                aggregatedCell: (info) => (
                  <span className="fw-bold">
                    {getTableValue(fieldMode, info.getValue())}
                  </span>
                ),
                minSize: 160,
                enableColumnFilter: false,
                meta: {
                  className: "text-end",
                  headerClassName: "text-end",
                },
              },
            ),
          )),
    ],
    [accounts, columnHelper, fieldMode, isExpanded],
  );

  const groupedColumns = useMemo(
    () => [
      columnHelper.group({
        id: "category",
        header: "Category",
        columns: [
          columnHelper.accessor((row) => row.riskAllocation, {
            id: "riskAllocation",
            header: "Risk Category",
            cell: (info) => (
              <span className="fw-bold">{capitalize(info.getValue())}</span>
            ),
            minSize: 180,
            size: 180,
            enableColumnFilter: false,
            meta: {
              className: "position-sticky",
              headerClassName: "position-sticky z-1",
              headerStyle: { left: 0 },
              style: { left: 0 },
            },
          }),
        ],
      }),
      ...flatColumns,
    ],
    [columnHelper, flatColumns],
  );

  const accountsWithHoldings = useMemo(
    () =>
      createRowsFromHoldings(
        holdings,
        transactions,
        modelSummaries,
        riskCategories,
        { oneRowPerSymbol },
      ),
    [holdings, modelSummaries, transactions, riskCategories, oneRowPerSymbol],
  );

  const filteredHoldingsForField = useMemo(
    () =>
      fieldMode === "realizedGainLoss" ||
      fieldMode === "income" ||
      (keepZeroValueInModelWeight &&
        (fieldMode === "modelWeight" || fieldMode === "modelWeightDifference"))
        ? accountsWithHoldings
        : accountsWithHoldings.filter((row) => row.totals.value !== 0),
    [accountsWithHoldings, fieldMode, keepZeroValueInModelWeight],
  );

  const { table } = useTable({
    columns: grouped === "riskAllocation" ? groupedColumns : flatColumns,
    data: filteredHoldingsForField,
    initialState: {
      sorting: [
        { id: "categoryOrder", desc: false },
        { id: "totalValue", desc: true },
      ],
      grouping: typeof grouped === "undefined" ? undefined : ["riskAllocation"],
      pagination: {
        pageSize: 9999,
      },
    },
    autoResetExpanded: false,
    manualPagination: true,
    getRowId: (row) =>
      `${row.riskAllocation}-${getRowKey(row, oneRowPerSymbol)}`,
  });

  useEffect(() => {
    if (accountsWithHoldings.length > 0) {
      table.toggleAllRowsExpanded(true);
    }
  }, [accountsWithHoldings, grouped, table]);

  return isLoading ? (
    <Loading message="Holdings" />
  ) : filteredHoldingsForField.length <= 0 ? (
    <Alert>No holdings found</Alert>
  ) : (
    <Table
      table={table}
      disablePagination
      isDisableGrouping={typeof grouped === "undefined"}
      isFixedGrouping
      className="w-auto"
    />
  );
};

export default HoldingsByAccountTable;
